// Helper functions to group data by different time periods

/**
 * Get the start of the week for a given date (Monday)
 */
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
};

/**
 * Get the start of the month for a given date
 */
const getMonthStart = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

/**
 * Format date as YYYY-MM-DD
 */
const formatDate = (date) => {
  return date.toISOString().split('T')[0];
};

/**
 * Format week label (e.g., "Week of Jan 15")
 */
const formatWeekLabel = (date) => {
  const d = new Date(date);
  return `Week of ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

/**
 * Format month label (e.g., "January 2026")
 */
const formatMonthLabel = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

/**
 * Group daily data into weekly buckets
 */
export const groupByWeek = (data) => {
  const weekMap = new Map();

  data.forEach(item => {
    const itemDate = new Date(item.date);
    const weekStart = getWeekStart(itemDate);
    const weekKey = formatDate(weekStart);

    if (weekMap.has(weekKey)) {
      weekMap.get(weekKey).count += item.count;
    } else {
      weekMap.set(weekKey, {
        date: weekKey,
        label: formatWeekLabel(weekStart),
        count: item.count
      });
    }
  });

  return Array.from(weekMap.values()).sort((a, b) =>
    new Date(a.date) - new Date(b.date)
  );
};

/**
 * Group daily data into monthly buckets
 */
export const groupByMonth = (data) => {
  const monthMap = new Map();

  data.forEach(item => {
    const itemDate = new Date(item.date);
    const monthStart = getMonthStart(itemDate);
    const monthKey = formatDate(monthStart);

    if (monthMap.has(monthKey)) {
      monthMap.get(monthKey).count += item.count;
    } else {
      monthMap.set(monthKey, {
        date: monthKey,
        label: formatMonthLabel(monthStart),
        count: item.count
      });
    }
  });

  return Array.from(monthMap.values()).sort((a, b) =>
    new Date(a.date) - new Date(b.date)
  );
};

/**
 * Fill missing dates in daily data with count = 0
 * Ensures every date between min and max has an entry
 */
export const fillDateGaps = (data) => {
  if (!data || data.length === 0) return data;

  const dateMap = new Map();
  data.forEach(item => {
    const key = item.date.split('T')[0];
    dateMap.set(key, item);
  });

  const dates = Array.from(dateMap.keys()).sort();
  const startDate = new Date(dates[0]);
  const endDate = new Date(dates[dates.length - 1]);
  const filled = [];

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().split('T')[0];
    if (dateMap.has(key)) {
      filled.push(dateMap.get(key));
    } else {
      filled.push({ date: key, count: 0 });
    }
  }

  return filled;
};

/**
 * Add labels to daily data
 */
export const addDailyLabels = (data) => {
  return data.map(item => ({
    ...item,
    label: new Date(item.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }));
};

/**
 * Group daily language-breakdown data into weekly buckets
 * Each item has { date, count, languageCounts: { ar: 5, en: 3 } }
 */
export const groupByWeekWithLanguages = (data) => {
  const weekMap = new Map();

  data.forEach(item => {
    const itemDate = new Date(item.date);
    const weekStart = getWeekStart(itemDate);
    const weekKey = formatDate(weekStart);

    if (weekMap.has(weekKey)) {
      const existing = weekMap.get(weekKey);
      existing.count += item.count;
      Object.entries(item.languageCounts || {}).forEach(([lang, cnt]) => {
        existing.languageCounts[lang] = (existing.languageCounts[lang] || 0) + cnt;
      });
    } else {
      weekMap.set(weekKey, {
        date: weekKey,
        label: formatWeekLabel(weekStart),
        count: item.count,
        languageCounts: { ...(item.languageCounts || {}) }
      });
    }
  });

  return Array.from(weekMap.values()).sort((a, b) =>
    new Date(a.date) - new Date(b.date)
  );
};

/**
 * Group daily language-breakdown data into monthly buckets
 */
export const groupByMonthWithLanguages = (data) => {
  const monthMap = new Map();

  data.forEach(item => {
    const itemDate = new Date(item.date);
    const monthStart = getMonthStart(itemDate);
    const monthKey = formatDate(monthStart);

    if (monthMap.has(monthKey)) {
      const existing = monthMap.get(monthKey);
      existing.count += item.count;
      Object.entries(item.languageCounts || {}).forEach(([lang, cnt]) => {
        existing.languageCounts[lang] = (existing.languageCounts[lang] || 0) + cnt;
      });
    } else {
      monthMap.set(monthKey, {
        date: monthKey,
        label: formatMonthLabel(monthStart),
        count: item.count,
        languageCounts: { ...(item.languageCounts || {}) }
      });
    }
  });

  return Array.from(monthMap.values()).sort((a, b) =>
    new Date(a.date) - new Date(b.date)
  );
};

/**
 * Fill missing dates in language-breakdown daily data with count = 0
 */
export const fillDateGapsWithLanguages = (data) => {
  if (!data || data.length === 0) return data;

  const dateMap = new Map();
  data.forEach(item => {
    const key = item.date.split('T')[0];
    dateMap.set(key, item);
  });

  const dates = Array.from(dateMap.keys()).sort();
  const startDate = new Date(dates[0]);
  const endDate = new Date(dates[dates.length - 1]);
  const filled = [];

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().split('T')[0];
    if (dateMap.has(key)) {
      filled.push(dateMap.get(key));
    } else {
      filled.push({ date: key, count: 0, languageCounts: {} });
    }
  }

  return filled;
};

/**
 * Calculate number of days between two dates
 */
export const getDaysBetween = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};
